import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getApiErrorMessage } from '~/utils/getApiErrorMessage'

export interface FreeBet {
  id: string
  betNumber: number
  amount: number       // XAUT
  amountUsd: number
  expiresAt: string
  usedAt: string | null
  status: string
}

interface FreeBetApiItem {
  betNumber: number
  status: string
  amountUsd: number
  amountXaut: string | null
  unlockedAt: string | null
  usedAt: string | null
  expiresAt: string | null
}

// Coalesce concurrent fetches and skip refetches within FETCH_DEBOUNCE_MS of
// the last successful response. Without this, multiple <FreeBetEarn> instances
// (header pill + BetPanel compact-bet) plus the post-login fetch in useAuth.ts
// fire 2-3 parallel GET /free-bets on every page load, blowing through the
// shared AUTH_GENERAL per-IP bucket and 429-ing unrelated endpoints.
const FETCH_DEBOUNCE_MS = 1000

export const useFreeBetsStore = defineStore('freeBets', () => {
  const config = useRuntimeConfig()

  // State
  const freeBets = ref<FreeBet[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Dedup state (module-local to the store instance, not exposed).
  let inflight: Promise<void> | null = null
  let lastFetchedAt = 0

  // Getters
  const availableFreeBets = computed(() =>
    freeBets.value.filter(fb => fb.status === 'available' && !fb.usedAt && fb.amount > 0 && new Date(fb.expiresAt) > new Date())
  )

  const hasFreeBets = computed(() => availableFreeBets.value.length > 0)

  const nextFreeBet = computed(() => availableFreeBets.value[0] || null)

  const freeBetCount = computed(() => availableFreeBets.value.length)

  // Actions
  async function fetchFreeBets(accessToken?: string): Promise<void> {
    // Debounce: a fresh response landed less than FETCH_DEBOUNCE_MS ago, skip.
    // Failures intentionally do NOT bump lastFetchedAt so a retry is allowed.
    if (Date.now() - lastFetchedAt < FETCH_DEBOUNCE_MS) return
    // Coalesce: another fetch is in flight — await the same Promise.
    if (inflight) return inflight

    inflight = (async () => {
      isLoading.value = true
      error.value = null

      try {
        const response = await $fetch<{ freeBets: FreeBetApiItem[] }>(`${config.public.apiBase}/free-bets`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        })
        freeBets.value = response.freeBets
          .filter((fb: FreeBetApiItem) => fb.amountXaut && fb.expiresAt)
          .map((fb: FreeBetApiItem) => ({
            id: `freebet-${fb.betNumber}`,
            betNumber: fb.betNumber,
            amount: parseFloat(fb.amountXaut!) || 0,
            amountUsd: fb.amountUsd,
            expiresAt: fb.expiresAt!,
            usedAt: fb.usedAt,
            status: fb.status,
          }))
        lastFetchedAt = Date.now()
      } catch (err) {
        console.error('[freeBetsStore] Failed to fetch free bets:', err)
        error.value = getApiErrorMessage(err, 'Failed to fetch free bets')
      } finally {
        isLoading.value = false
        inflight = null
      }
    })()
    return inflight
  }

  function markUsed(freeBetId: string): void {
    const fb = freeBets.value.find(f => f.id === freeBetId)
    if (fb) {
      fb.usedAt = new Date().toISOString()
    }
  }

  /**
   * Optimistically restore any free bet we marked as used locally back to
   * available, used after a free-bet cancel succeeds. The server-side row is
   * already restored at this point; the upcoming `fetchFreeBets()` refetch
   * will confirm. Without this, hasFreeBets stays false until the refetch
   * resolves, causing a brief Bet/Play-FREE button flicker after cancel.
   *
   * Heuristic: a locally-marked-used free bet has `status='available'` (server
   * hasn't pushed `'used'` yet over realtime) AND `usedAt != null` (we set it
   * via markUsed()). At any moment at most one free bet matches — only one
   * place call is in flight per slot, and slot 1 is the only free-bet slot.
   */
  function restoreLastLocallyUsed(): void {
    const fb = freeBets.value.find(f => f.status === 'available' && f.usedAt !== null)
    if (fb) {
      fb.usedAt = null
    }
  }

  function setFreeBets(newFreeBets: FreeBet[]): void {
    freeBets.value = newFreeBets
  }

  function clearFreeBets(): void {
    freeBets.value = []
    error.value = null
  }

  return {
    // State
    freeBets,
    isLoading,
    error,
    // Getters
    availableFreeBets,
    hasFreeBets,
    nextFreeBet,
    freeBetCount,
    // Actions
    fetchFreeBets,
    markUsed,
    restoreLastLocallyUsed,
    setFreeBets,
    clearFreeBets
  }
})
